import { useState } from "react"
import { getAccessToken, getAdminAccessToken } from '../utils/methods'
import { useNavigate } from "react-router-dom"
import { Card, CardContent } from "../components/ui/card"
import { useEffectOnce } from '../hooks/useEffectOnce'

/**
 * Token Page - Handles OAuth callbacks for both normal users and admins
 * Normal users: /token?code=xxx (uses OAuth App)
 * Admins: /admin/token?code=xxx (uses GitHub App)
 */
const TokenPage = ({ isAdmin = false }) => {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const code = window.location.search.split('=')[1]
    const navigate = useNavigate()

    useEffectOnce(() => {
        const auth = window.localStorage.getItem('auth')
        
        // Determine redirect path based on admin status
        const successRedirect = isAdmin ? '/admin' : '/dashboard'
        const errorRedirect = isAdmin ? '/admin/login' : '/'
        
        // If already authenticated, redirect appropriately
        if (auth) {
            const authData = JSON.parse(auth)
            // For admin routes, check if user is admin
            if (isAdmin && !authData.user?.isAdmin && !authData.user?.isSuperAdmin) {
                navigate(errorRedirect, { replace: true })
                return
            }
            navigate(successRedirect, { replace: true })
            return
        }

        // If no code, redirect to appropriate page
        if (!code) {
            navigate(errorRedirect, { replace: true })
            return
        }

        // Process the authorization code with appropriate method
        const tokenPromise = isAdmin ? getAdminAccessToken(code) : getAccessToken(code)
        
        tokenPromise
            .then((res) => {
                console.log('Authentication successful');
                window.localStorage.setItem('auth', JSON.stringify(res.data))
                
                // Also store the token separately for easy access
                if (res.data.token) {
                    window.localStorage.setItem('token', res.data.token)
                }
                if (res.data.githubToken) {
                    window.localStorage.setItem('githubToken', res.data.githubToken)
                }
                
                setLoading(false)
                // Redirect after successful authentication
                setTimeout(() => {
                    navigate(successRedirect, { replace: true })
                }, 500)
            })
            .catch((err) => {
                console.error('Authentication error:', err);
                const errorMessage = err.response?.data?.message || 'Authentication failed. Please try again.'
                setError(errorMessage)
                setLoading(false)
                // Redirect after 3 seconds on error
                setTimeout(() => {
                    navigate(errorRedirect, { replace: true })
                }, 3000)
            })
    })

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <Card className="w-full max-w-md">
                <CardContent className="pt-6">
                    {loading ? (
                        <div className="text-center space-y-4">
                            <div className="flex justify-center">
                                <div className="h-16 w-16 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                            </div>
                            <div className="space-y-2">
                                <h2 className="text-xl font-semibold">Authenticating...</h2>
                                <p className="text-sm text-muted-foreground">Please wait while we verify your credentials</p>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="text-center space-y-4">
                            <div className="flex justify-center">
                                <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
                                    <svg className="h-8 w-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <h2 className="text-xl font-semibold text-destructive">Authentication Failed</h2>
                                <p className="text-sm text-muted-foreground">{error}</p>
                                <p className="text-xs text-muted-foreground">Redirecting to home...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center space-y-4">
                            <div className="flex justify-center">
                                <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center">
                                    <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <h2 className="text-xl font-semibold text-green-500">Success!</h2>
                                <p className="text-sm text-muted-foreground">Redirecting to dashboard...</p>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

/**
 * Admin Token Page - Wrapper for admin OAuth callback
 */
const AdminTokenPage = () => {
    return <TokenPage isAdmin={true} />
}

export {
    TokenPage,
    AdminTokenPage,
}